import Gap from '@/src/components/Modules-revamp/Gap';
import ModuleWidth from '@/src/components/Modules-revamp/ModuleWidthSelect';
import ResponsiveResizingOptionSelect from '@/src/components/Modules-revamp/ResponsiveResizingOptionSelect';
import TabType from '@/src/components/Modules-revamp/TabType';
import Wrapper from 'src/components/Modules-revamp/BlockWrapper';
import BlockTitle from '@/src/components/Modules-revamp/BlockTitle';
import Animation from 'src/components/Modules-revamp/Animation';
import {
  ResponsiveContainer,
  ResponsiveField,
  formAnimationList,
  formButtonList,
} from '@/src/strategies/util';
import { EPreviewSize } from '@/src/types/common/SalesChannel';
import React from 'react';
import TabStyle from '@/src/components/Modules-revamp/TabStyle';
import PosterTabEdit from '@/src/components/Modules-revamp/PosterTabEdit';
import { get } from 'lodash';
import AllTabs from '@/src/components/Modules-revamp/AllTabs';
import { EnumModuleType } from '../../../../pages/SalesChannelContentEditor/common';
import moduleStyleRender from '../../ModuleStyle';
import posterTabRenderTemplate from '@/src/strategies/render/posterTab';
import { ContentModuleGroupPreloadingContext } from '@/src/constrains/DraggableModules/PreloadComponent/SubscriptionPreloading';
import { Field } from '@/src/components/RcForm';
import { usePosterTab } from '../../hooks/usePosterTab';
import { checkResponsivePosterTab } from 'src/constrains/SalesChannelContent-Revamp/ModuleList/validator';
import { posters, tabs } from 'src/tools/contentModulesFieldName';

type ResponsiveFieldProps = {
  field: ResponsiveField | ResponsiveField[];
  disabled?: boolean;
  sliderDisabled?: boolean;
  options?: Array<{ label: string; value: any }>;
  hasModal?: boolean;
  modalFunc?: any;
  extra?: any;
};

const posterTabRenderer = (form: any, breakpoint: any, extra: any) => {
  const values = form.getFieldsValue(true);
  const { handleOnSectionReset, handleOnSectionCopy, salesChannelMode } = extra;
  const list = posterTabRenderTemplate(values, breakpoint, salesChannelMode);
  const renderComponents = (
    {
      type,
      field,
    }: {
      title: string;
      type?: ResponsiveContainer;
      field: Array<any>;
    },
    breakpoint: any,
  ) => {
    if (type === ResponsiveContainer.ModuleStyle) {
      return field.map((f) => moduleStyleRender(f));
    } else {
      return field.map((f) => posterTabComponent(f, breakpoint, extra));
    }
  };

  return (
    <>
      {list.map((item) => {
        const component = renderComponents(item, breakpoint);
        return (
          <Wrapper key={item.title} id={`${item?.id}`} breakpoint={breakpoint}>
            <BlockTitle
              id={item?.id}
              title={item?.title}
              breakpoint={breakpoint}
              onReset={() => handleOnSectionReset(item?.fields, breakpoint)}
              onCopy={() => handleOnSectionCopy(item?.fields, breakpoint)}
              headerErrorDependencies={get(item, 'headerErrorDependencies')}
              validator={get(item, 'validator')}
            />
            {component}
          </Wrapper>
        );
      })}
    </>
  );
};

const posterTabComponent = (
  data: ResponsiveFieldProps,
  breakpoint: EPreviewSize,
  extra: any,
) => {
  const { animations, hoverAnimations, form, buttons } = extra || {};
  const draggableId = get(form.getFieldsValue(true), 'draggableId');
  const {
    onSort,
    onDelete,
    onAdd,
    onTabPosterSort,
    onTypeChange,
    onModalConfirm,
    tabValidator,
  } = extra;
  switch (data.field) {
    case ResponsiveField.TabType: {
      return (
        <TabType disabled={data?.disabled} options={data?.options || []} />
      );
    }
    case ResponsiveField.ModuleWidth: {
      return (
        <ModuleWidth disabled={data?.disabled} options={data?.options || []} />
      );
    }
    case ResponsiveField.Gap: {
      return <Gap disabled={data?.disabled} options={data?.options || []} />;
    }
    case ResponsiveField.ResizingOption: {
      return (
        <ResponsiveResizingOptionSelect
          disabled={data?.disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.Animation: {
      return (
        <Animation
          disabled={data?.disabled}
          animationOptions={formAnimationList(animations || [])}
          hoverEffectOptions={formAnimationList(hoverAnimations || [])}
        />
      );
    }
    case ResponsiveField.TabStyle: {
      return (
        <TabStyle
          disabled={data?.disabled}
          sliderDisabled={data?.sliderDisabled}
        />
      );
    }
    case ResponsiveField.AllTabs: {
      return (
        <AllTabs
          disabled={data?.disabled}
          draggableId={draggableId}
          onSort={onSort}
          onAdd={onAdd}
          onDelete={onDelete}
          moduleType={EnumModuleType.ResponsivePosterTab}
          breakpoint={breakpoint}
        />
      );
    }
    case ResponsiveField.TabBarSetting: {
      return (
        <>
          <BlockTitle
            disabled={data?.disabled}
            title={data?.extra?.title}
            breakpoint={EPreviewSize.WIDE}
            validator={(d, extra) => {
              const tbs = get(d, tabs);
              const tab = get(tbs, data?.extra?.index);
              return tabValidator(tab, extra);
            }}
            headerErrorDependencies={[[tabs, data?.extra?.index, posters]]}
          />
          <PosterTabEdit
            onModalConfirm={onModalConfirm}
            disabled={data?.disabled}
            options={data?.options || []}
            buttons={formButtonList(buttons)}
            animationOptions={formAnimationList(animations)}
            hoverEffectOptions={formAnimationList(hoverAnimations)}
            breakpoint={breakpoint}
            index={data?.extra?.index}
            tabDraggableId={data?.extra?.tabDraggableId}
            onSort={onTabPosterSort}
            onTypeChange={onTypeChange}
            hasModal={data?.hasModal}
            modalFunc={data?.modalFunc}
          />
        </>
      );
    }
  }
};

export default ({ form, breakpoint, ...extra }) => {
  const {
    handleOnModalConfirm,
    handleOnAddTab,
    handleOnDeleteTab,
    handleOnSortTab,
    handleOnTabPosterSort,
    handleOnTypeChange,
  } = usePosterTab({ form });
  const { animations, hoverAnimations, buttons } = React.useContext(
    ContentModuleGroupPreloadingContext,
  );
  const { checkEachTabError } = checkResponsivePosterTab();
  return (
    <Field shouldUpdate>
      {() =>
        posterTabRenderer(form, breakpoint, {
          onModalConfirm: handleOnModalConfirm,
          onAdd: handleOnAddTab,
          onDelete: handleOnDeleteTab,
          onSort: handleOnSortTab,
          onTabPosterSort: handleOnTabPosterSort,
          onTypeChange: handleOnTypeChange,
          animations,
          buttons,
          hoverAnimations,
          form,
          ...extra,
          tabValidator: checkEachTabError,
        })
      }
    </Field>
  );
};
